import * as React from 'react';
import * as acl from 'common/utils/acl';

import { AppState } from 'common/models/AppState';
import { Member } from 'common/api/models/Member';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { OrgAccess } from 'common/api/models/OrgAccess';

import { AddressBlock } from 'common/components/AddressBlock';
import { Datetime } from 'common/components/Datetime';
import { EditableTags } from 'common/components/EditableTags';
import { Email } from 'common/components/Email';
import { Phone } from 'common/components/Phone';
import { SocialLinks } from 'common/components/SocialLinks';
import { MemberLink } from 'common/components/link/MemberLink';
import { Badge } from 'common/components/Badge';
import { Tag } from 'common/api/models/Tag';
import { Feature } from 'common/features/Feature';
import { canViewBadges } from 'common/features/canViewBadges';
import moment = require('moment');
import { ChapterPosition } from 'common/api/models/ChapterPosition';

export interface UsersDetailInfoProps {
  member: Member;
  appState: AppState;
  chapterPositions: ChapterPosition[];
  onUpdateTags: (tagIds: string[]) => void;
  onUpdateAssignedTags: (tagIds: string[]) => void;
  onUpdateInternalTags: (tagIds: string[]) => void;
}

export class UsersDetailInfo extends React.Component<UsersDetailInfoProps> {
  pluckChapterHistory(data: ChapterPosition[]) {
    let arr = [];
    for (var chapterPosition of data) {
      var year = new Date(chapterPosition.startDate ? chapterPosition.startDate : '').getFullYear();
      arr.push(year + ' ' + chapterPosition.positionName);
    }
    return arr.join(', ');
  }
  render() {
    const {
      appState: { entities },
      member,
      onUpdateTags,
      onUpdateInternalTags,
      onUpdateAssignedTags,
    } = this.props;
    const profileUrl = member.slug ? `${process.env.REACT_APP_WEB_URL}/${member.slug}` : undefined;

    return (
      <div>
        <div className="row" style={{ marginTop: 20 }}>
          <div className="col-2 text-right">
            <b>Created</b>
          </div>
          <div className="col">
            <Datetime at={member.createAt} /> by <MemberLink member={entities.member[member.createId!]} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Updated</b>
          </div>
          <div className="col">
            <Datetime at={member.updateAt} /> by <MemberLink member={entities.member[member.updateId!]} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Last Login</b>
          </div>
          <div className="col">
            <Datetime at={member.loginAt} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Member Profile</b>
          </div>
          <div className="col">
            {profileUrl && (
              <a href={profileUrl} target="_blank">
                {profileUrl}
              </a>
            )}
            {!profileUrl && <span>None</span>}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Member Type</b>
          </div>
          <div className="col">{member.memberType}</div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Wiki Access</b>
          </div>
          <div className="col">{member.vars.wiki ? member.vars.wiki : 'NONE'}</div>
        </div>
        {member && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Wiki Renewal Date</b>
            </div>
            <div className="col">
              {member.wikiExpireDate ? <Datetime at={member.wikiExpireDate} format="MMMM D, YYYY" /> : 'NONE'}
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-2 text-right">
            <b>Global Connect Access</b>
          </div>
          <div className="col">{member.vars.globalConnect ? member.vars.globalConnect : 'NONE'}</div>
        </div>
        {member.vars.globalConnectExpireDate && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Global Connect Expire Date</b>
            </div>
            <div className="col">
              {/* <Datetime at={ moment(member.vars.globalConnectExpireDate).utc().format() } format="MMMM D, YYYY" /> */}
              <Datetime
                at={moment(member.vars.globalConnectExpireDate)
                  .utc()
                  .format('YYYY-MM-DD')}
                format="MMMM D, YYYY"
              />
            </div>
          </div>
        )}
        {member && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Renewal Date</b>
            </div>
            <div className="col">
              <Datetime at={member.expireDate} format="MMMM D, YYYY" />
            </div>
          </div>
        )}
        {member && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Renewal Type</b>
            </div>
            <div className="col">
              {member.renewType && member.renewType === Member.RenewType.AUTO
                ? 'Auto-Renew'
                : member.renewType && member.renewType === Member.RenewType.MANUAL
                  ? 'Manual'
                  : 'Invoice'}
            </div>
          </div>
        )}
        {member && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Certification Renewal Date</b>
            </div>
            <div className="col">
              <Datetime at={member.certificationExpireDate} format="MMMM D, YYYY" />
            </div>
          </div>
        )}
        <div className="row" style={{ marginTop: 20 }}>
          <div className="col-2 text-right">
            <b>Social Profiles</b>
          </div>
          <div className="col">{member ? <SocialLinks social={member.social} /> : <span>None</span>}</div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Email</b>
          </div>
          <div className="col">
            <Email email={member.email!} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Secondary Email</b>
          </div>
          <div className="col">
            <Email email={member.secondaryEmail!} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Home Phone</b>
          </div>
          <div className="col">
            <Phone phone={member.homePhone} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Work Phone</b>
          </div>
          <div className="col">
            <Phone phone={member.workPhone} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Mobile Phone</b>
          </div>
          <div className="col">
            <Phone phone={member.mobilePhone} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Home Address</b>
          </div>
          <div className="col">
            <AddressBlock address={member.homeAddress} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Work Address</b>
          </div>
          <div className="col">
            <AddressBlock address={member.workAddress} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Mailing Address</b>
          </div>
          <div className="col">
            <AddressBlock address={member.shippingAddress} />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Billing Address</b>
          </div>
          <div className="col">
            <AddressBlock address={member.billingAddress} />
          </div>
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <div className="col-2 text-right">
            <b>Orgs</b>
          </div>
          <div className="col">
            {member.orgs!.length === 0 && <div>None</div>}
            {member.orgs!.map(o => (
              <div key={o.memberId}>
                <MemberLink member={entities.member[o.memberId!]} />{' '}
                {o.access === OrgAccess.Access.ADMIN && <Badge type="primary" text="Admin" />}
                {o.access === OrgAccess.Access.NORMAL && <Badge type="info" text="Normal" />}
              </div>
            ))}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Segment</b>
          </div>
          <div className="col">
            {member.segment && member.segment === 'BUILDING'
              ? 'Building and/or Remodeling'
              : member.segment && member.segment === 'COLLEGE'
                ? 'College / University'
                : member.segment && member.segment === 'CABINET'
                  ? 'Cabinet Shop'
                  : member.segment && member.segment === 'DEALER'
                    ? 'Dealer / Showroom'
                    : member.segment && member.segment === 'DESIGN'
                      ? 'Design'
                      : member.segment && member.segment === 'IP'
                        ? 'Industry Partner'
                        : member.segment && member.segment === 'MANUFACTURING'
                          ? 'Manufacturing'
                          : member.segment && member.segment === 'RETAIL'
                            ? 'Retail'
                            : member.segment && member.segment === 'SKILLED'
                              ? 'Skilled Trades'
                              : member.segment && member.segment === 'WHOLESALE'
                                ? 'Wholesale'
                                : member.segment && member.segment === 'NONE'
                                  ? 'No Segment Assigned'
                                  : member.segment && 'Other'}
            {!member.segment && 'No Segment Assigned'}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Functional Role</b>
          </div>
          <div className="col">
            {member.functionalRole && member.functionalRole === 'NONE'
              ? 'None'
              : member.functionalRole && member.functionalRole === 'ARCHITECT'
                ? 'Architect'
                : member.functionalRole && member.functionalRole === 'BUILDER'
                  ? 'Builder'
                  : member.functionalRole && member.functionalRole === 'DESIGNER'
                    ? 'Designer'
                    : member.functionalRole && member.functionalRole === 'FABRICATOR'
                      ? 'Fabricator'
                      : member.functionalRole && member.functionalRole === 'GM'
                        ? 'General Manager'
                        : member.functionalRole && member.functionalRole === 'INSTALLER'
                          ? 'Installer'
                          : member.functionalRole && member.functionalRole === 'PROJECT'
                            ? 'Project Coordinator / Manager'
                            : member.functionalRole && member.functionalRole === 'REMODELER'
                              ? 'Remodeler'
                              : member.functionalRole && member.functionalRole === 'SALESPERSON'
                                ? 'Salesperson'
                                : member.functionalRole && member.functionalRole === 'SHOWROOM'
                                  ? 'Showroom Manager / Staff'
                                  : member.functionalRole && member.functionalRole === 'SKILLED'
                                    ? 'Skilled Trades Professional'
                                    : member.functionalRole && member.functionalRole === 'HOME'
                                      ? 'Home Technology Integrator'
                                      : member.functionalRole && member.functionalRole === 'OTHER'
                                        ? 'Other'
                                        : member.functionalRole && member.functionalRole === 'EDUCATOR'
                                          ? 'Educator'
                                          : member.functionalRole && member.functionalRole === 'STUDENT'
                                            ? 'Student'
                                            : member.functionalRole && member.functionalRole === 'APPRENTICE'
                                              ? 'Apprentice'
                                              : member.functionalRole && member.functionalRole === 'MASTER'
                                                ? 'Master Carpenter'
                                                : member.functionalRole && member.functionalRole === 'BATH'
                                                  ? 'Bath Designer'
                                                  : member.functionalRole && member.functionalRole === 'INTERIOR'
                                                    ? 'Interior Designer'
                                                    : member.functionalRole && member.functionalRole === 'KITCHEN'
                                                      ? 'Kitchen Designer'
                                                      : member.functionalRole && member.functionalRole === 'LIGHTING'
                                                        ? 'Lighting Designer'
                                                        : member.functionalRole &&
                                                          member.functionalRole === 'INDEPENDENTSHOWROOM'
                                                          ? 'Independent K&B Showroom Manager / Staff'
                                                          : member.functionalRole &&
                                                            member.functionalRole === 'CORPORATE'
                                                            ? 'Corporate Role (Marketing, Product Development, Purchasing, etc.)'
                                                            : member.functionalRole &&
                                                              member.functionalRole === 'CUSTOMER'
                                                              ? 'Customer Service / Product Training / Technical Support'
                                                              : member.functionalRole &&
                                                                member.functionalRole === 'INDEPENDENT'
                                                                ? 'Independent Manufacturer Rep (representing multiple brands)'
                                                                : member.functionalRole &&
                                                                  member.functionalRole === 'DEDICATED'
                                                                  ? 'Dedicated Manufacturer Rep / Factory Rep (representing a single brand)'
                                                                  : member.functionalRole &&
                                                                    member.functionalRole === 'MULTIBRANCH'
                                                                    ? 'Multi-Branch Retailer Manager / Staff'
                                                                    : member.functionalRole &&
                                                                      member.functionalRole === 'ONLINE'
                                                                      ? 'Online K&B Seller Manager / Staff'
                                                                      : member.functionalRole &&
                                                                        member.functionalRole === 'ELECTRICIAN'
                                                                        ? 'Electrician'
                                                                        : member.functionalRole &&
                                                                          member.functionalRole === 'CARPENTER'
                                                                          ? 'Carpenter'
                                                                          : member.functionalRole &&
                                                                            member.functionalRole === 'PLUMBER'
                                                                            ? 'Plumber'
                                                                            : member.functionalRole &&
                                                                              member.functionalRole === 'PURCHASING'
                                                                              ? 'Purchasing'
                                                                              : member.functionalRole &&
                                                                                member.functionalRole ===
                                                                                  'MERCHANDISING'
                                                                                ? 'Merchandising'
                                                                                : member.functionalRole &&
                                                                                  member.functionalRole === 'WAREHOUSE'
                                                                                  ? 'Warehouse Manager'
                                                                                  : member.functionalRole &&
                                                                                    member.functionalRole ===
                                                                                      'WHOLESALE'
                                                                                    ? 'Wholesale Manager / Staff'
                                                                                    : member.functionalRole &&
                                                                                      member.functionalRole ===
                                                                                        'WHOLESALESHOWROOM'
                                                                                      ? 'Wholesale Showroom Manager / Staff'
                                                                                      : member.functionalRole &&
                                                                                        'Other'}
            {!member.functionalRole && 'None'}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Organization Level</b>
          </div>
          <div className="col">
            {member.organizationLevel && member.organizationLevel === 'OWNER'
              ? 'Owner / Partner'
              : member.organizationLevel && member.organizationLevel === 'PRESIDENT'
                ? 'President / C-Suite Executive'
                : member.organizationLevel && member.organizationLevel === 'EXECUTIVE'
                  ? 'Executive (SVP, EVP, VP)'
                  : member.organizationLevel && member.organizationLevel === 'MIDDLE'
                    ? 'Middle Management (Director / Manager)'
                    : member.organizationLevel && member.organizationLevel === 'STAFF'
                      ? 'Staff Role'
                      : member.organizationLevel && member.organizationLevel === 'ADMINISTRATIVE'
                        ? 'Administrative Role'
                        : member.organizationLevel && member.organizationLevel === 'OTHER'
                          ? 'Other'
                          : member.organizationLevel && 'No Organization Level Assigned'}
            {!member.organizationLevel && 'No Organization Level Assigned'}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Chapter</b>
          </div>
          <div className="col">
            {member.chapterId && entities.chapter[member.chapterId] && entities.chapter[member.chapterId].name}
            {!member.chapterId && 'None/Foreign'}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Chapter Role</b>
          </div>
          <div className="col">
            {(!member.chapterPosition || member.chapterPosition!.roleName === Member.ChapterRole.NONE) && 'None'}
            {member.chapterPosition && member.chapterPosition!.roleName === Member.ChapterRole.MEMBER && 'Member'}
            {member.chapterPosition &&
              member.chapterPosition!.roleName === Member.ChapterRole.OFFICER &&
              'Officer (' + member.chapterPosition!.positionName + ')'}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Join Date</b>
          </div>
          <div className="col">
            <Datetime at={member.joinDate} format="MMMM D, YYYY" />
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Certification</b>
          </div>
          <div className="col">{member.vars.certification === 'NONE' ? 'None' : member.vars.certification}</div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Educ. Hours</b>
          </div>
          <div className="col">{member.vars.educationHours}</div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>CEU Credits</b>
          </div>
          <div className="col">{member.vars.ceuCredits ? Number(member.vars.ceuCredits).toFixed(2) : '0.0'}</div>
        </div>
        {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) &&
          (member.vars.badgeMeetings || member.vars.badgeMeetingsVirtual) && (
            <div className="row">
              <div className="col-2 text-right">
                <b>Badge Credits</b>
              </div>
              <div className="col">
                {member.vars.badgeMeetings ? member.vars.badgeMeetings : 0} in-person,{' '}
                {member.vars.badgeMeetingsVirtual ? member.vars.badgeMeetingsVirtual : 0} virtual
              </div>
            </div>
          )}
        {member.vars.gradDate && (
          <div className="row">
            <div className="col-2 text-right">
              <b>Grad Date</b>
            </div>
            <div className="col">
              <Datetime at={member.vars.gradDate} format="MMM D YYYY" />
            </div>
          </div>
        )}
        {member.vars.kbis && (
          <div className="row">
            <div className="col-2 text-right">
              <b>KBIS Code</b>
            </div>
            <div className="col">{member.vars.kbis}</div>
          </div>
        )}
        <div className="row">
          <div className="col-2 text-right">
            <b>Tags</b>
          </div>
          <div className="col-8">
            <EditableTags
              tags={this.props.appState.entities.tag}
              tagIds={member.tagIds!}
              onSave={onUpdateTags}
              canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
            />
          </div>
        </div>
        {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) ? (
          <div className="row">
            <div className="col-2 text-right">
              <b>Badges</b>
            </div>
            <div className="col-8">
              <EditableTags
                tags={this.props.appState.entities.tag}
                tagIds={member.assignedTags ? member.assignedTags.map(t => t.tagId!) : []}
                type={Tag.Type.BADGE}
                onSave={onUpdateAssignedTags}
                canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
                label="Edit Badges"
              />
            </div>
          </div>
        ) : null}
        <div className="row">
          <div className="col-2 text-right">
            <b>Chapter History</b>
          </div>
          <div className="col-8">
            {this.props.chapterPositions ? this.pluckChapterHistory(this.props.chapterPositions) : ''}
          </div>
        </div>
        <div className="row">
          <div className="col-2 text-right">
            <b>Internal Tags</b>
          </div>
          <div className="col-8">
            <EditableTags
              tags={this.props.appState.entities.tag}
              tagIds={member.internalTagIds!}
              type={Tag.Type.INTERNAL}
              onSave={onUpdateInternalTags}
              canEdit={acl.hasPermission(this.props.appState, UserPermissions.Permissions.EDIT_MEMBER_TAGS)}
            />
          </div>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/UsersDetailInfo.tsx